import { $delegate } from 'fxdom/es';
import { pipe } from 'fxjs/es';
import { UtilS } from '../../Util/S/Function/module/UtilS.js';
import {
  CUSTOM_SELECT_BOX_CN,
  CUSTOM_TEXT_INPUT_BOX_CN,
  USER_CUSTOM_PROPERTY_MAPPING_PATH,
} from '../S/constant.js';
import { makeCustomSelectorHtml, makeCustomTextInputHtml } from '../S/custom_select.js';

// 유저 커스텀 속성 선택 변경 시 실행될 핸들러
const userCustomPropertyPathChanged = ({ id, text, value, isReset }) => {
  const active_item = G.mp.maker.active();

  if (active_item == null) return;

  const existingData = active_item._data[USER_CUSTOM_PROPERTY_MAPPING_PATH] || {};
  const { id: _, text: __, value: ___, ...rest } = existingData;

  if (isReset) {
    active_item._data[USER_CUSTOM_PROPERTY_MAPPING_PATH] = rest;
  } else {
    Object.assign(active_item._data, {
      [USER_CUSTOM_PROPERTY_MAPPING_PATH]: { ...rest, id, text, value },
    });
  }
};

const userCustomPropertyTemplateChanged = ({ value }) => {
  const active_item = G.mp.maker.active();

  if (active_item == null) return;
  if (USER_CUSTOM_PROPERTY_MAPPING_PATH in active_item._data) {
    active_item._data[USER_CUSTOM_PROPERTY_MAPPING_PATH].template = value;
  } else {
    active_item._data[USER_CUSTOM_PROPERTY_MAPPING_PATH] = { template: value };
  }
};

export const getUserCustomPropertyMappingPathFromBox = () => {
  const active_item = G.mp.maker.active();
  if (active_item == null) return null;

  return active_item?._data[USER_CUSTOM_PROPERTY_MAPPING_PATH];
};

// 선택 변경 시 실행될 로직을 클래스명에 따라 처리하는 함수
const handleSelectChange = (selectBoxEl, { id, value, text, isReset }) => {
  const classList = Array.from(selectBoxEl.classList);

  switch (true) {
    case classList.includes(CUSTOM_SELECT_BOX_CN.USER_CUSTOM_PROPERTY_PATHS):
      userCustomPropertyPathChanged({ id, text, value, isReset });
      break;
    default:
      break;
  }
};

const handlePropertyInputChange = (value) => {
  userCustomPropertyTemplateChanged({ value });
};

export const mpMakerCustomInputBoxEventInit = pipe(
  $delegate('input', '.custom-text-input-box input', ({ currentTarget: ct }) => {
    const value = ct.value;
    handlePropertyInputChange(value);
  }),
);

export const mpMakerCustomSelectBoxEventInit = pipe(
  // select-change 커스텀 이벤트 리스너
  $delegate('select-change', '.custom-select-box', (e) => {
    const selectBoxEl = e.target;
    handleSelectChange(selectBoxEl, e.detail);
  }),

  $delegate('click', '.custom-select-box .select-selected', ({ currentTarget: ct }) => {
    const selectBox = ct.closest('.custom-select-box');

    // 다른 열린 셀렉트 박스들 닫기
    document.querySelectorAll('.custom-select-box.active').forEach((box) => {
      if (box !== selectBox) box.classList.remove('active');
    });

    // 현재 셀렉트 박스 토글
    selectBox.classList.toggle('active');
  }),

  // 옵션 선택 이벤트
  $delegate('click', '.custom-select-box .select-option', ({ currentTarget: ct }) => {
    const selectBox = ct.closest('.custom-select-box');
    const selectedDiv = selectBox.querySelector('.select-selected');
    const allOptions = selectBox.querySelectorAll('.select-option');

    const DEFAULT_TEXT = '선택하세요';
    const RESET_CLASS = 'select-option-reset';

    // 모든 옵션에서 selected 클래스 제거
    allOptions.forEach((option) => option.classList.remove('selected'));

    let selectedValue, selectedText, selectedId;

    if (ct.classList.contains(RESET_CLASS)) {
      // 초기화 옵션 선택 시
      const resetValues = {
        text: DEFAULT_TEXT,
        value: '',
        id: '',
      };

      // selectedDiv 업데이트
      selectedDiv.textContent = resetValues.text;
      selectedDiv.dataset.value = resetValues.value;
      selectedDiv.dataset.id = resetValues.id;

      // 선택값 설정
      selectedValue = resetValues.value;
      selectedText = resetValues.text;
      selectedId = resetValues.id;
    } else {
      // 일반 옵션 선택 시
      const selectedValues = {
        text: ct.textContent,
        value: ct.dataset.value,
        id: ct.dataset.id,
      };

      // selectedDiv 업데이트
      selectedDiv.textContent = selectedValues.text;
      selectedDiv.dataset.value = selectedValues.value;
      selectedDiv.dataset.id = selectedValues.id;

      // 선택된 옵션 하이라이트
      ct.classList.add('selected');

      // 선택값 설정
      selectedValue = selectedValues.value;
      selectedText = selectedValues.text;
      selectedId = selectedValues.id;
    }

    // 셀렉트 박스 닫기
    selectBox.classList.remove('active');

    // 선택 이벤트 발생
    const event = new CustomEvent('select-change', {
      bubbles: true,
      detail: {
        id: selectedId,
        value: selectedValue,
        text: selectedText.trim().replace(/\n/g, ''),
        isReset: ct.classList.contains(RESET_CLASS),
      },
    });

    selectBox.dispatchEvent(event);
  }),

  // 마플라이저 클릭 시 열려 있는 셀렉트 박스 닫기
  $delegate('click', '#marpplizer', (e) => {
    if (!e.target.closest('.custom-select-box')) {
      document.querySelectorAll('.custom-select-box.active').forEach((box) => {
        box.classList.remove('active');
      });
    }
  }),
);

export const makeUserCustomSelectorHtml = () => {
  // 유저 커스텀 메이커 일때만 속성 편집 노출
  const user_custom_property_mapping_paths = box.sel('user_custom_property_mapping_paths');

  if (UtilS.isEmpty(user_custom_property_mapping_paths)) return '';

  const user_custom_property_mapping_path = getUserCustomPropertyMappingPathFromBox();

  const items = user_custom_property_mapping_paths.map(({ id, name, path }) => ({
    id,
    name,
    value: path,
    is_selected: parseInt(user_custom_property_mapping_path?.id) === id,
  }));

  return makeCustomSelectorHtml({
    title: '속성 연결',
    items,
    className: CUSTOM_SELECT_BOX_CN.USER_CUSTOM_PROPERTY_PATHS,
  });
};

export const makeUserCustomTextPropertyInputHtml = () => {
  // 유저 커스텀 메이커 텍스트 편집 일때만 속성 편집 노출
  const user_custom_property_mapping_path = getUserCustomPropertyMappingPathFromBox();

  return makeCustomTextInputHtml({
    title: '속성 문자템플릿',
    value: user_custom_property_mapping_path?.template ?? '',
    className: CUSTOM_TEXT_INPUT_BOX_CN.USER_CUSTOM_PROPERTY_TEMPLATE,
    placeholder: '치환될 문자는 $$$ 로 적어주세요.',
  });
};
